import { computed, type Ref } from "vue";
import { storeToRefs } from "pinia";
import { useResultsV2Store } from "@stores/survey/resultsV2/resultsV2";
import { useCardLocalOptions } from "@/composables/useCardLocalOptions";
import type { ChartCard, DisplayedPopulation, ResultsCard, ResultsView } from "@/types";
import {
  chartTileOptions,
  chartTilePatch,
  resolveChartTile,
  resolveChartType,
} from "@/components/Survey/ResultsV2/cardOptionRules";

export function useChartTile(
  card: Ref<ResultsCard>,
  questionMeta: Ref<ChartCard | null | undefined>,
  displayedPopulations: Ref<DisplayedPopulation[]>,
) {
  const { currentView } = storeToRefs(useResultsV2Store());
  const { updateLocalOption } = useCardLocalOptions(card);

  const viewOptions = computed(() => (currentView.value as ResultsView | null)?.options || {});
  const hasSubtotals = computed(() => !!card.value?.options?.subtotals_enabled);
  const chartContext = computed(() => ({
    multiPopulation: displayedPopulations.value.length > 1,
    hasSubtotals: hasSubtotals.value,
  }));

  const tiles = computed(() => chartTileOptions(questionMeta.value, chartContext.value));
  const tile = computed(() =>
    resolveChartTile(questionMeta.value, card.value?.options || {}, viewOptions.value, chartContext.value),
  );
  const chartType = computed(() =>
    resolveChartType(questionMeta.value, card.value?.options || {}, viewOptions.value, chartContext.value),
  );

  function selectTile(value: string) {
    const patch = chartTilePatch(value, questionMeta.value, viewOptions.value);
    for (const [key, val] of Object.entries(patch)) updateLocalOption(key, val);
  }

  return { viewOptions, hasSubtotals, chartContext, tiles, tile, chartType, selectTile };
}
